import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useDirecionadaById,
	useUpdateDirecionada,
} from '@/api/queries/direcionada/direcionada-queries'
import {
	useExportMyPendencies,
	useMyPendencies,
} from '@/api/queries/pendencies-queries'
import { usePeople } from '@/api/queries/people-queries'
import {
	useUpdateVereAgir,
	useVereAgirById,
} from '@/api/queries/ver-e-agir-queries'
import { CorrectDirectedFormContent } from '@/components/administration/correct-directed-form'
import { CorrectSeeActFormContent } from '@/components/administration/correct-see-act-form'
import {
	ResponsiveTable,
	type ResponsiveTableColumn,
} from '@/components/administration/kpi-metrics/shared/responsive-table'
import {
	AccessDeniedState,
	AdminCardPage,
	ColumnsMenu,
	type ExportFormat,
	ExportMenu,
	ResponsiveFormModal,
} from '@/components/commons'
import { InspectionListFilters } from '@/components/inspections'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { ROUTES } from '@/constants/routes'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useDeviceType } from '@/hooks/useDeviceType'
import { useProfileStore } from '@/store'
import { useAuthStore } from '@/store/auth-store'
import type { InspectionType, Role, VereAgirUpdateInput } from '@/types'
import type { DirecionadaUpdateInput } from '@/types/direcionada'
import type { PendencyRawItem } from '@/types/pendency'
import { getApiErrorMessage, isForbiddenApiError } from '@/utils/api-error'
import { capitalize } from '@/utils/capitalize'
import { getDeadlineStatus } from '@/utils/deadline'
import { downloadBlob } from '@/utils/download-blob'
import { formatDateToDisplay } from '@/utils/formatDate'

type EditingItem = {
	id: string
	type: PendingInspectionType
}

type PendingListItem = {
	id: string
	inspectionId: string
	type: PendingInspectionType
	crId: string
	date: string
	dueDate: string
	crName: string
	branchName: string
	subject: string
	detail: string
	description: string
	responsibleId: string
	responsibleName: string
	createdByName: string
}

type PendingInspectionType = Extract<InspectionType, 'directed' | 'see-act'>

type InspectionConfig = {
	titleModal: string
	renderForm: () => React.ReactNode
}

const COLUMNS_STORAGE_KEY = 'ssma-actions-in-progress-columns'

const AUTHORIZED_ROLES: Role[] = ['TECHNICIAN', 'SSMA_MANAGER', 'ADMIN']

const TYPE_MAP: Record<string, PendingInspectionType> = {
	direcionada: 'directed',
	DIRECIONADA: 'directed',
	ver_e_agir: 'see-act',
	VER_E_AGIR: 'see-act',
	directed: 'directed',
	seeandact: 'see-act',
	see_and_act: 'see-act',
	'see-act': 'see-act',
}

const TYPE_TO_API_SOURCE_TYPE: Record<PendingInspectionType, string> = {
	directed: 'DIRECIONADA',
	'see-act': 'VER_E_AGIR',
}

const toText = (value: string | undefined): string => {
	if (!value) {
		return '-'
	}
	return capitalize(value)
}

const normalizeType = (
	value: string | undefined,
): PendingInspectionType | null => {
	if (!value) {
		return null
	}
	const key = value.trim().replaceAll(' ', '_')
	return TYPE_MAP[key] ?? null
}

const mapPendencyToListItem = (
	item: PendencyRawItem,
): PendingListItem | null => {
	const type = normalizeType(
		item.sourceType ?? item.inspectionType ?? item.type,
	)
	if (!type) {
		return null
	}

	const isSeeAct = type === 'see-act'
	const quizTitle = item.meta?.quizTitle ?? item.quizTitle
	const question = item.meta?.question ?? item.question
	const actionPlan = item.meta?.actionPlan ?? item.actionPlan

	return {
		id: item.id,
		inspectionId: item.sourceId ?? item.inspectionId ?? item.id,
		type,
		crId: item.crId ?? '',
		date: item.date ?? '',
		dueDate: item.dueDate ?? '',
		crName: item.crName ?? '-',
		branchName: item.branchName ?? '-',
		subject: toText(
			isSeeAct ? (item.meta?.kindName ?? item.kindName) : quizTitle,
		),
		detail: toText(
			isSeeAct ? (item.meta?.riskName ?? item.riskName) : question,
		),
		description: toText(
			isSeeAct ? item.description : (actionPlan ?? item.description),
		),
		responsibleId: item.responsible?.id ?? '',
		responsibleName: item.responsible?.name ?? '',
		createdByName: item.createdBy?.name ?? '',
	}
}

const parseTimestamp = (value: string) => {
	const timestamp = Date.parse(value)
	return Number.isNaN(timestamp) ? 0 : timestamp
}

const dedupeDirectedPendencies = (items: PendingListItem[]) => {
	const unique = new Map<string, PendingListItem>()

	for (const item of items) {
		if (item.type !== 'directed') {
			unique.set(`${item.type}:${item.id}`, item)
			continue
		}

		const key = `${item.type}:${item.inspectionId}`
		const current = unique.get(key)
		if (!current || parseTimestamp(item.date) > parseTimestamp(current.date)) {
			unique.set(key, item)
		}
	}

	return Array.from(unique.values())
}

export const ActionsInProgressPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { user } = useAuthStore()
	const { defaultBranchId, defaultCustomerId } = useProfileStore()
	const canManageBranchFilter =
		user?.role === 'ADMIN' ||
		user?.role === 'MANAGER' ||
		user?.role === 'SSMA_MANAGER'
	const isAuthorizedToResolve = user
		? AUTHORIZED_ROLES.includes(user.role)
		: false

	const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>()
	const [selectedType, setSelectedType] = useState<
		PendingInspectionType | undefined
	>()
	const [selectedCrId, setSelectedCrId] = useState<string | undefined>()
	const [selectedResponsibleId, setSelectedResponsibleId] = useState<
		string | undefined
	>()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch, isPending: isSearchPending } =
		useDebouncedValue(search, 600)
	const [editingItem, setEditingItem] = useState<EditingItem | null>(null)
	const [saveIsLoading, setSaveIsLoading] = useState(false)

	const resolvedBranchId =
		(canManageBranchFilter ? selectedBranchId : undefined) ||
		defaultBranchId ||
		undefined

	const pendenciesParams = useMemo(
		() => ({
			scope: resolvedBranchId
				? ('branch' as 'branch' | 'mine')
				: ('mine' as 'branch' | 'mine'),
			activeBranchId: resolvedBranchId,
			q: debouncedSearch.trim() || undefined,
			sourceType: selectedType
				? TYPE_TO_API_SOURCE_TYPE[selectedType]
				: undefined,
			crId: selectedCrId,
			customerId: defaultCustomerId || undefined,
		}),
		[
			resolvedBranchId,
			debouncedSearch,
			selectedType,
			selectedCrId,
			defaultCustomerId,
		],
	)

	const {
		data: pendenciesData,
		isLoading,
		isError,
		error,
	} = useMyPendencies(pendenciesParams)
	const { data: peopleData } = usePeople()

	const updateMutationDirecionada = useUpdateDirecionada()
	const updateMutationVereAgir = useUpdateVereAgir()
	const exportMutation = useExportMyPendencies()
	const { isMobile } = useDeviceType()

	const directedInspectionId =
		editingItem?.type === 'directed' ? editingItem.id : undefined
	const seeActInspectionId =
		editingItem?.type === 'see-act' ? editingItem.id : undefined
	const { data: directedSelectedData, isLoading: isLoadingDirectedSelected } =
		useDirecionadaById(directedInspectionId)
	const { data: seeActSelectedData, isLoading: isLoadingSeeActSelected } =
		useVereAgirById(seeActInspectionId)

	const pendencies = useMemo<PendingListItem[]>(() => {
		const rawItems = pendenciesData?.items ?? []
		const mappedItems = rawItems
			.map(mapPendencyToListItem)
			.filter((item): item is PendingListItem => item !== null)

		return dedupeDirectedPendencies(mappedItems)
	}, [pendenciesData])

	const fallbackFacets = useMemo(() => {
		const typeCount = new Map<PendingInspectionType, number>()
		const contractCount = new Map<
			string,
			{ id: string; name: string; count: number }
		>()
		const responsibleCount = new Map<
			string,
			{ id: string; name: string; count: number }
		>()

		for (const pendency of pendencies) {
			typeCount.set(pendency.type, (typeCount.get(pendency.type) ?? 0) + 1)
			if (pendency.crId && pendency.crName && pendency.crName !== '-') {
				const contract = contractCount.get(pendency.crId)
				if (contract) {
					contract.count += 1
				} else {
					contractCount.set(pendency.crId, {
						id: pendency.crId,
						name: pendency.crName,
						count: 1,
					})
				}
			}
			if (pendency.responsibleId && pendency.responsibleName) {
				const responsible = responsibleCount.get(pendency.responsibleId)
				if (responsible) {
					responsible.count += 1
				} else {
					responsibleCount.set(pendency.responsibleId, {
						id: pendency.responsibleId,
						name: pendency.responsibleName,
						count: 1,
					})
				}
			}
		}

		return {
			types: Array.from(typeCount.entries()).map(([value, count]) => ({
				value,
				count,
			})),
			branches: [],
			contracts: Array.from(contractCount.values()),
			responsibles: Array.from(responsibleCount.values()),
		}
	}, [pendencies])

	const facets = useMemo(() => {
		const apiFacets = pendenciesData?.facets
		if (!apiFacets) {
			return fallbackFacets
		}

		const mappedTypes = (apiFacets.sourceTypes ?? [])
			.map((sourceType) => {
				const normalizedType = normalizeType(sourceType.value)
				if (!normalizedType) return null
				return {
					value: normalizedType,
					count: sourceType.count,
				}
			})
			.filter(
				(item): item is { value: PendingInspectionType; count: number } =>
					item !== null,
			)

		return {
			types: mappedTypes,
			branches: apiFacets.branches ?? fallbackFacets.branches,
			contracts: apiFacets.crs ?? [],
			responsibles: fallbackFacets.responsibles,
		}
	}, [pendenciesData?.facets, fallbackFacets])

	const filteredPendencies = useMemo(() => {
		return pendencies
			.filter((pendency) => {
				if (selectedType && pendency.type !== selectedType) {
					return false
				}

				if (selectedCrId && pendency.crId !== selectedCrId) {
					return false
				}

				if (
					selectedResponsibleId &&
					pendency.responsibleId !== selectedResponsibleId
				) {
					return false
				}

				return true
			})
			.sort((a, b) => parseTimestamp(b.date) - parseTimestamp(a.date))
	}, [pendencies, selectedType, selectedCrId, selectedResponsibleId])

	const getTypeLabel = (type: PendingInspectionType) => {
		if (type === 'directed') {
			return t('inspections.types.directed.title')
		}
		return t('inspections.types.seeAndAct.title')
	}

	const filterSelects = [
		...(canManageBranchFilter
			? [
					{
						id: 'actions-in-progress-filter-branch',
						label: t('inspections.common.branch'),
						value: selectedBranchId,
						onChange: (value: string) => {
							setSelectedBranchId(value || undefined)
							setSelectedCrId(undefined)
						},
						options: facets.branches.map((branchFacet) => ({
							value: branchFacet.id,
							label: branchFacet.name,
							count: branchFacet.count,
						})),
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
		{
			id: 'actions-in-progress-filter-type',
			label: t('inspections.actionsInProgress.kindLabel'),
			value: selectedType,
			onChange: (value: string) =>
				setSelectedType(
					(value || undefined) as PendingInspectionType | undefined,
				),
			options: facets.types.map((typeFacet) => ({
				value: typeFacet.value,
				label: getTypeLabel(typeFacet.value),
				count: typeFacet.count,
			})),
			allLabel: t('inspections.directed.filterAll'),
		},
		{
			id: 'actions-in-progress-filter-contract',
			label: t('inspections.directed.filterByContract'),
			value: selectedCrId,
			onChange: (value: string) => setSelectedCrId(value || undefined),
			options: facets.contracts.map((contractFacet) => ({
				value: contractFacet.id,
				label: contractFacet.name,
				count: contractFacet.count,
			})),
			allLabel: t('inspections.directed.filterAll'),
		},
		{
			id: 'actions-in-progress-filter-responsible',
			label: t('inspections.actionsInProgress.responsibleLabel'),
			value: selectedResponsibleId,
			onChange: (value: string) => setSelectedResponsibleId(value || undefined),
			options: facets.responsibles.map((responsibleFacet) => ({
				value: responsibleFacet.id,
				label: responsibleFacet.name,
				count: responsibleFacet.count,
			})),
			allLabel: t('inspections.directed.filterAll'),
		},
	]

	const handleSaveDirected = async (data: DirecionadaUpdateInput) => {
		if (!editingItem || editingItem.type !== 'directed') {
			return
		}

		setSaveIsLoading(true)
		try {
			await updateMutationDirecionada.mutateAsync({
				id: editingItem.id,
				data: {
					...data,
					...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				},
			})
			toast.success(t('administration.correctInspection.directedEditSuccess'))
			setEditingItem(null)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		} finally {
			setSaveIsLoading(false)
		}
	}

	const handleSaveSeeAct = async (data: VereAgirUpdateInput) => {
		if (!editingItem || editingItem.type !== 'see-act') {
			return
		}

		setSaveIsLoading(true)
		try {
			await updateMutationVereAgir.mutateAsync({
				id: editingItem.id,
				data: {
					...data,
					...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				},
			})
			toast.success(t('administration.correctInspection.seeActEditSuccess'))
			setEditingItem(null)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		} finally {
			setSaveIsLoading(false)
		}
	}

	const inspectionConfig: Record<PendingInspectionType, InspectionConfig> = {
		directed: {
			titleModal: t('administration.correctInspection.titleModalDirect'),
			renderForm: () => {
				if (isLoadingDirectedSelected) {
					return (
						<div className="py-8 text-center text-muted-foreground">
							{t('common.loading')}
						</div>
					)
				}
				return (
					<>
						{!isAuthorizedToResolve && (
							<Alert variant="destructive" className="mb-4">
								<AlertCircle className="h-4 w-4" />
								<AlertDescription>
									{t('inspections.actionsInProgress.unauthorizedMessage')}
								</AlertDescription>
							</Alert>
						)}
						<CorrectDirectedFormContent
							directedSelected={directedSelectedData}
							statusOnly
							onSave={handleSaveDirected}
							isLoading={saveIsLoading}
							onClose={() => setEditingItem(null)}
							isDisabled={!isAuthorizedToResolve}
						/>
					</>
				)
			},
		},
		'see-act': {
			titleModal: t('administration.correctInspection.titleModalSeeAct'),
			renderForm: () => {
				if (isLoadingSeeActSelected) {
					return (
						<div className="py-8 text-center text-muted-foreground">
							{t('common.loading')}
						</div>
					)
				}
				return (
					<>
						{!isAuthorizedToResolve && (
							<Alert variant="destructive" className="mb-4">
								<AlertCircle className="h-4 w-4" />
								<AlertDescription>
									{t('inspections.actionsInProgress.unauthorizedMessage')}
								</AlertDescription>
							</Alert>
						)}
						<CorrectSeeActFormContent
							seeActSelected={seeActSelectedData}
							persons={peopleData ?? []}
							statusOnly
							onSave={handleSaveSeeAct}
							isLoading={saveIsLoading}
							onClose={() => setEditingItem(null)}
							isDisabled={!isAuthorizedToResolve}
						/>
					</>
				)
			},
		},
	}

	const activeEditConfig = editingItem
		? inspectionConfig[editingItem.type]
		: null

	const handleExport = (format: ExportFormat) => {
		if (format === 'csv') return
		exportMutation.mutate(
			{
				params: { ...pendenciesParams, responsibleId: selectedResponsibleId },
				format,
			},
			{
				onSuccess: (blob) =>
					downloadBlob(
						blob,
						`acoes-em-andamento-${new Date().toISOString().slice(0, 10)}.${format}`,
					),
				onError: () => toast.error(t('common.exportError')),
			},
		)
	}

	const renderDeadline = (dueDate: string) => {
		const status = getDeadlineStatus(dueDate)
		if (status.kind === 'none') {
			return (
				<span className="text-muted-foreground">
					{t('inspections.actionsInProgress.deadlineNone')}
				</span>
			)
		}
		if (status.kind === 'onTime') {
			return (
				<Badge className="bg-kaefer-grass-green/15 text-kaefer-forest-green">
					{t('inspections.actionsInProgress.deadlineOnTime')}
				</Badge>
			)
		}
		return (
			<Badge className="bg-kaefer-red/15 text-kaefer-red">
				{status.days === 1
					? t('inspections.actionsInProgress.deadlineOverdueOne')
					: t('inspections.actionsInProgress.deadlineOverdueMany', {
							count: status.days,
						})}
			</Badge>
		)
	}

	const tableColumns: ResponsiveTableColumn<PendingListItem>[] = [
		{
			key: 'type',
			header: t('inspections.actionsInProgress.typeColumn'),
			render: (row) => getTypeLabel(row.type),
			className: 'whitespace-nowrap',
		},
		{
			key: 'date',
			header: t('inspections.actionsInProgress.dateColumn'),
			render: (row) => (row.date ? formatDateToDisplay(row.date) : '-'),
			className: 'whitespace-nowrap',
		},
		{
			key: 'branch',
			header: t('inspections.actionsInProgress.branchColumn'),
			render: (row) => row.branchName,
			className: 'max-w-36 truncate',
		},
		{
			key: 'cr',
			header: t('inspections.actionsInProgress.contractColumn'),
			render: (row) => row.crName,
			className: 'max-w-40 truncate',
		},
		{
			key: 'subject',
			header: t('inspections.actionsInProgress.subjectColumn'),
			render: (row) => row.subject,
			className: 'max-w-44 truncate',
		},
		{
			key: 'detail',
			header: t('inspections.actionsInProgress.detailColumn'),
			render: (row) => row.detail,
			className: 'max-w-48 truncate',
		},
		{
			key: 'description',
			header: t('inspections.actionsInProgress.descriptionColumn'),
			render: (row) => row.description,
			className: 'max-w-72 truncate',
		},
		{
			key: 'responsible',
			header: t('inspections.actionsInProgress.responsibleColumn'),
			render: (row) => row.responsibleName || '-',
			className: 'max-w-40 truncate',
		},
		{
			key: 'createdBy',
			header: t('inspections.actionsInProgress.createdByColumn'),
			render: (row) => row.createdByName || '-',
			className: 'max-w-40 truncate',
		},
		{
			key: 'dueDate',
			header: t('inspections.actionsInProgress.dueDateColumn'),
			render: (row) => (row.dueDate ? formatDateToDisplay(row.dueDate) : '-'),
			className: 'whitespace-nowrap',
		},
		{
			key: 'deadline',
			header: t('inspections.actionsInProgress.deadlineColumn'),
			render: (row) => renderDeadline(row.dueDate),
			hideOnCard: true,
		},
	]

	const columnVisibility = useColumnVisibility(
		COLUMNS_STORAGE_KEY,
		tableColumns.map((column) => column.key),
	)
	// no mobile a lista vira cards com todos os campos, sem escolha de colunas
	const visibleColumns = isMobile
		? tableColumns
		: tableColumns.filter((column) => columnVisibility.isVisible(column.key))

	if (isForbiddenApiError(error)) {
		return <AccessDeniedState />
	}

	return (
		<AdminCardPage
			icon={<CheckCircle2 className="size-5" />}
			title={t('inspections.types.actionsInProgress.title')}
			subtitle={t('inspections.types.actionsInProgress.description')}
			onBack={() => navigate(ROUTES.INSPECTIONS.INDEX)}
			footerText={`${filteredPendencies.length} ${
				filteredPendencies.length === 1
					? t('administration.correctInspection.countSingular')
					: t('administration.correctInspection.countPlural')
			}`}
			actions={
				<div className="flex w-full gap-2 sm:w-auto sm:shrink-0 [&>button]:flex-1 sm:[&>button]:flex-none">
					{!isMobile && (
						<ColumnsMenu
							columns={tableColumns.map((column) => ({
								key: column.key,
								label: column.header,
							}))}
							isVisible={columnVisibility.isVisible}
							onToggle={columnVisibility.toggle}
							onReset={columnVisibility.reset}
							hasHidden={columnVisibility.hasHidden}
						/>
					)}
					<ExportMenu
						onExport={handleExport}
						isExporting={exportMutation.isPending}
						disabled={isLoading || filteredPendencies.length === 0}
					/>
				</div>
			}
		>
			<div className="space-y-4">
				<InspectionListFilters
					searchValue={search}
					onSearchChange={setSearch}
					selects={filterSelects}
				/>

				{isSearchPending && (
					<div className="py-4 text-center text-sm text-muted-foreground">
						{t('common.loading')}
					</div>
				)}

				{isLoading && (
					<div className="py-8 text-center text-muted-foreground">
						{t('common.loading')}
					</div>
				)}

				{isError && (
					<div className="py-8 text-center text-destructive">
						{t('inspections.actionsInProgress.loadError')}
					</div>
				)}

				{!isLoading && !isError && filteredPendencies.length === 0 && (
					<div className="py-8 text-center text-muted-foreground">
						{t('inspections.actionsInProgress.empty')}
					</div>
				)}

				{!isLoading && !isError && filteredPendencies.length > 0 && (
					<div className="sm:max-h-[65dvh] sm:overflow-auto sm:rounded-md sm:border sm:border-kaefer-light-silver [&_[data-slot=table-container]]:overflow-visible sm:[&_thead]:sticky sm:[&_thead]:top-0 sm:[&_thead]:z-10 sm:[&_th]:bg-white">
						<ResponsiveTable
							columns={visibleColumns}
							rows={filteredPendencies}
							rowKey={(row) => row.id}
							cardTitleKey="subject"
							cardBadge={(row) => renderDeadline(row.dueDate)}
							onRowClick={(row) =>
								setEditingItem({ id: row.inspectionId, type: row.type })
							}
						/>
					</div>
				)}
			</div>

			<ResponsiveFormModal
				open={!!editingItem}
				onOpenChange={(isOpen) => {
					if (!isOpen) {
						setEditingItem(null)
					}
				}}
				title={activeEditConfig?.titleModal ?? ''}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
			>
				{editingItem && activeEditConfig ? activeEditConfig.renderForm() : null}
			</ResponsiveFormModal>
		</AdminCardPage>
	)
}
